<template>
  <div class="two-factor-switch">
    <h3 class="body-bold-base">
      {{ $t('message.pages.users.twoFactor.switch.title') }}
    </h3>
    <div class="switch-row">
      <template v-if="hasSyncedState">
        <v-switch
          :input-value="switchState"
          @click.stop.prevent="onSwitchClick"
          :key="switchRenderKey"
          :loading="loadingUpdate"
          :disabled="loadingUpdate || !isRoot"
          class="switch"
          hide-details
        />
      </template>
      <template v-else>
        <v-skeleton-loader type="avatar" class="switch-skeleton" />
      </template>
      <span class="label body-regular-small">
        {{ $t('message.pages.users.twoFactor.switch.label') }}
        <v-menu
          v-model="infoVisible"
          open-on-hover
          open-on-click
          offset-y
          transition="scale-transition"
          :close-on-content-click="false"
          max-width="300"
        >
          <template #activator="{ on, attrs }">
            <v-icon small class="info-icon" size="16" v-bind="attrs" v-on="on">
              panda-info-solid
            </v-icon>
          </template>
          <v-card class="tooltip-card">
            <v-card-text>
              {{ $t('message.pages.users.twoFactor.switch.info') }}
            </v-card-text>
            <v-card-actions>
              <base-btn type="tertiary" @click="handleMoreInfo">
                {{ $t('message.actions.knowMore') }}
                <v-icon class="ml-2" size="16"
                  >mdi-open-in-new</v-icon
                ></base-btn
              >
            </v-card-actions>
          </v-card>
        </v-menu>
      </span>
    </div>
    <enable-2FA-modal
      v-model="showEnableModal"
      @confirmed="confirmEnable"
      @cancelEnable="cancelEnable"
    />
    <create-2FA-modal
      v-model="showCreateModal"
      @confirmed="confirmCreate"
      @cancelCreate="cancelCreate"
    />
  </div>
</template>

<script>
import BaseBtn from '@/components/BaseBtn.vue'
import Enable2FAModal from './SettingsModals/Enable2faModal.vue'
import Create2FAModal from './SettingsModals/Create2faModal.vue'
import { mapState, mapMutations } from 'vuex'
import organizationApi from '@/services/organizationApi.js'
import i18n from '@/translations/lang'
export default {
  name: 'TwoFactorSwitch',
  components: { BaseBtn, Enable2FAModal, Create2FAModal },
  data() {
    return {
      isRequireMfaEnabled: false,
      infoVisible: false,
      showEnableModal: false,
      showCreateModal: false,
      switchState: false,
      hasSyncedState: false,
      switchRenderKey: 0,
      loadingUpdate: false,
    }
  },
  props: {
    usersList: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState({
      organization: (state) => state.user.organization,
    }),
    userAllInfo() {
      return this.$store.state.user.userAllInfo
    },
    userId() {
      return this.$store.state.user.userAllInfo.id
    },
    isRoot() {
      return this.userAllInfo?.is_root ?? false
    },
    allUsersRequireMfa() {
      return this.$store.state.user.organization?.require_mfa ?? false
    },
    hasAuthCreated() {
      const currentUser = this.usersList.find((user) => user.id === this.userId)
      return currentUser?.mfa_enabled ?? false
    },
  },
  watch: {
    organization: {
      deep: true,
      immediate: true,
      once: true,
      handler(org) {
        if (org !== null) {
          this.isRequireMfaEnabled = org.require_mfa
          this.switchState = org.require_mfa
          this.hasSyncedState = true
        }
      },
    },
  },
  methods: {
    ...mapMutations('user', [
      'UPDATE_GLOBAL_SNACKBAR',
      'UPDATE_USER_ORGANIZATION',
    ]),
    onSwitchClick() {
      if (this.switchState) {
        this.disableRequireMfa()
      }
      if (!this.switchState) {
        if (this.hasAuthCreated) {
          this.$root.$emit('openEnable2FAModal')
        } else {
          this.$root.$emit('openCreate2FAModal')
        }
      }
    },
    async confirmEnable() {
      try {
        this.loadingUpdate = true
        const { data: updatedData } = await organizationApi.updateOrganization({
          require_mfa: true,
        })
        updatedData.require_mfa = true
        this.UPDATE_USER_ORGANIZATION(updatedData)
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: i18n.t(
            'message.pages.users.twoFactor.snackBar.enableRequireMfa.success'
          ),
          type: 'success',
        })
      } catch (error) {
        console.error(error)
        this.switchState = false
        this.switchRenderKey += 1
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: i18n.t(
            'message.pages.users.twoFactor.snackBar.enableRequireMfa.fail'
          ),
          type: 'error',
        })
      } finally {
        this.loadingUpdate = false
      }
    },
    async disableRequireMfa() {
      try {
        this.loadingUpdate = true

        const { data: updatedData } = await organizationApi.updateOrganization({
          require_mfa: false,
        })
        updatedData.require_mfa = false
        this.UPDATE_USER_ORGANIZATION(updatedData)
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: i18n.t(
            'message.pages.users.twoFactor.snackBar.disableRequireMfa.success'
          ),
          type: 'success',
        })
        this.switchRenderKey += 1
      } catch (error) {
        console.error(error)
        this.switchState = true
        this.switchRenderKey += 1
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: i18n.t(
            'message.pages.users.twoFactor.snackBar.disableRequireMfa.fail'
          ),
          type: 'error',
        })
      } finally {
        this.loadingUpdate = false
      }
    },
    cancelEnable() {
      if (this.loadingUpdate) return
      this.switchState = false
      this.switchRenderKey += 1
    },
    confirmCreate() {
      // this.isRequireMfaEnabled = true
      // this.switchState = true
    },
    cancelCreate() {
      this.switchState = false
      this.switchRenderKey += 1
    },
    handleMoreInfo() {
      this.infoVisible = false
      const link = this.$t('message.pages.users.twoFactor.articleLink')
      window.open(link, '_blank')
    },
  },
}
</script>

<style lang="scss" scoped>
.two-factor-switch {
  padding: $spacing-05;
  margin-top: $spacing-05;
  border-radius: 8px;
  border: 1px solid varFigma(neutral-95-lightest);
  width: 100%;

  h3.body-bold-base {
    color: varFigma(neutral-20-dark);
    margin-bottom: $spacing-03;
  }

  .switch-row {
    display: flex;
    align-items: center;
    gap: $spacing-03;

    .v-input.switch.v-input--selection-controls {
      margin-top: 0;
      padding-top: 0;
    }

    .label {
      color: varFigma(neutral-40-light);
    }
  }
}

.info-icon {
  margin-left: $spacing-03;
  cursor: pointer;
  &.theme--dark.v-icon {
    color: varFigma(neutral-70);
  }
}

.switch-skeleton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  width: 40px;
  height: 24px;
  border-radius: 14px;
  margin-right: 8px;
}
</style>
